import get from 'lodash/get';
import { ReactNode, useCallback } from 'react';
import { FieldValues } from 'react-hook-form';

type GetOptionAdornment<TOption extends FieldValues> = ((option: TOption) => ReactNode) | keyof TOption;

export const useAdornmentGetters = <TOption extends FieldValues>(
    getOptionStartAdornment?: GetOptionAdornment<TOption>,
    getOptionEndAdornment?: GetOptionAdornment<TOption>
) => {
    const getAdornment = useCallback(
        (option: TOption | null, adornmentGetter: GetOptionAdornment<TOption> | undefined) => {
            if (!option || !adornmentGetter) {
                return null;
            }

            if (typeof adornmentGetter === 'string') {
                return get(option, adornmentGetter);
            } else {
                return (adornmentGetter as (option: TOption) => ReactNode)(option);
            }
        },
        []
    );

    const getStartAdornment = useCallback(
        (option: TOption | null) => getAdornment(option, getOptionStartAdornment),
        [getAdornment, getOptionStartAdornment]
    );

    const getEndAdornment = useCallback(
        (option: TOption | null) => getAdornment(option, getOptionEndAdornment),
        [getAdornment, getOptionEndAdornment]
    );

    return { getStartAdornment, getEndAdornment };
};
